import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Customer } from "@acto/taskpane/core/domain";
import { ClientFilter, ClientOrderBy, ClientSearchMode } from "@acto/gql/graphql";
import { mapClientToCustomer } from "./mapClientToCustomer";

const ClientsQuery = graphql(`
  query GetClients($first: Int!, $after: String, $filter: ClientFilter) {
    clients(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface FilterOptions {
  /** Smart client search: name, city, postcode and customer number (typo-tolerant). */
  searchTerm?: string;
  externalReference?: string;
  orderBy?: ClientOrderBy;
}

interface Options extends PagingOptions, FilterOptions {
  enabled?: boolean;
}

export function useCustomers({
  limit = 5,
  searchTerm,
  orderBy,
  enabled = true,
}: Options): PaginatedDataAccessResult<Customer> {
  const filter: ClientFilter | undefined = useMemo(() => {
    if (!searchTerm && !orderBy) {
      return undefined;
    }
    return {
      orderBy,
      ...(searchTerm ? { searchTerm, searchMode: ClientSearchMode.Smart } : {}),
    };
  }, [searchTerm, orderBy]);

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: ClientsQuery,
    limit,
    variables: { filter },
    enabled,
    connection: (data) => data.clients,
  });

  const clients = useMemo(() => data?.map(mapClientToCustomer), [data]);

  return {
    data: clients,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
